import { ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { useEffect, useState } from 'react';

import { FlagIcon } from '@/components/FlagIcon';

import type { BriefingRow } from './api';
import { useBriefingRows } from './useBriefingRows';

interface BriefingRowPanelProps {
  selectedRowId: string | null;
  onRowSelect: (id: string | null) => void;
}

export function BriefingRowPanel({ selectedRowId, onRowSelect }: BriefingRowPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  // V1 is scoped to BRIEFING_ROW_SCOPE (Meta Image); when the page supports
  // other platforms/formats the scope will come from parent props or URL params.
  const { groupedByPlan, isLoading, isError, rows } = useBriefingRows();

  // Auto-expand the panel when a row is selected via URL
  useEffect(() => {
    if (selectedRowId && rows.some((r) => r.id === selectedRowId)) {
      setIsExpanded(true);
    }
  }, [selectedRowId, rows]);

  // Clear invalid row IDs once data has loaded successfully.
  // Guarded by !isError so a transient fetch failure doesn't wipe a valid selection.
  useEffect(() => {
    if (!isLoading && !isError && selectedRowId && !rows.some((r) => r.id === selectedRowId)) {
      onRowSelect(null);
    }
  }, [isLoading, isError, selectedRowId, rows, onRowSelect]);

  // Collapsed: thin vertical strip
  if (!isExpanded) {
    return (
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className="flex w-[72px] flex-shrink-0 flex-col items-center justify-center gap-2 -mb-3 -mr-4 -mt-3 self-stretch border-l border-slate-200 bg-white transition-colors hover:bg-slate-50"
        aria-label="Expand briefing rows"
        title="Expand briefing rows"
      >
        <Layers className="h-4 w-4 text-slate-400" />
      </button>
    );
  }

  return (
    <div className="flex w-[280px] flex-shrink-0 flex-col -mb-3 -mr-4 -mt-3 self-stretch border-l border-slate-200 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <div className="flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-[12.5px] font-semibold text-slate-700">Approved Rows</span>
          {rows.length > 0 && (
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
              {rows.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          className="rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          aria-label="Collapse briefing rows"
          title="Collapse"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <BriefingRowsSkeleton />
        ) : isError ? (
          <p className="px-2 py-4 text-center text-[12px] text-red-500">
            Failed to load briefing rows
          </p>
        ) : rows.length === 0 ? (
          <p className="px-2 py-6 text-center text-[12px] text-slate-400">No eligible rows</p>
        ) : (
          <PlanGroups
            groupedByPlan={groupedByPlan}
            selectedRowId={selectedRowId}
            onRowSelect={onRowSelect}
          />
        )}
      </div>
    </div>
  );
}

/** Accordion-style plan groups */
function PlanGroups({
  groupedByPlan,
  selectedRowId,
  onRowSelect,
}: {
  groupedByPlan: Map<string, BriefingRow[]>;
  selectedRowId: string | null;
  onRowSelect: (id: string | null) => void;
}) {
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(() => {
    // Auto-expand the group containing the selected row on mount
    if (!selectedRowId) return new Set<string>();
    for (const [planKey, planRows] of groupedByPlan) {
      if (planRows.some((r) => r.id === selectedRowId)) {
        return new Set([planKey]);
      }
    }
    return new Set<string>();
  });

  // When selectedRowId changes (e.g. from URL), ensure its group is expanded
  useEffect(() => {
    if (!selectedRowId) return;
    for (const [planKey, planRows] of groupedByPlan) {
      if (planRows.some((r) => r.id === selectedRowId)) {
        setExpandedPlans((prev) => {
          if (prev.has(planKey)) return prev;
          return new Set(prev).add(planKey);
        });
        break;
      }
    }
  }, [selectedRowId, groupedByPlan]);

  const toggle = (planKey: string) => {
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(planKey)) next.delete(planKey);
      else next.add(planKey);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-1">
      {Array.from(groupedByPlan.entries()).map(([planKey, planRows]) => {
        const isOpen = expandedPlans.has(planKey);
        const firstRow = planRows[0];
        return (
          <div key={planKey}>
            <button
              type="button"
              onClick={() => toggle(planKey)}
              className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left transition-colors hover:bg-slate-50"
            >
              <ChevronRight
                className={`h-3 w-3 flex-shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
              />
              <div className="min-w-0 flex-1">
                <span className="truncate text-[11.5px] font-semibold text-slate-600">
                  {planKey === 'no-plan' ? 'No Plan' : `Plan ${planKey}`}
                </span>
                {planKey !== 'no-plan' && (
                  <p className="flex items-center gap-1 text-[10px] text-slate-400">
                    <FlagIcon countryCode={firstRow.market} className="h-2.5 w-auto" />
                    {firstRow.quarter}
                  </p>
                )}
              </div>
            </button>
            {isOpen && (
              <div className="ml-3 flex flex-col gap-0.5 border-l border-slate-100 pl-2">
                {planRows.map((row) => {
                  const isSelected = row.id === selectedRowId;
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => onRowSelect(isSelected ? null : row.id)}
                      className={`w-full rounded px-2 py-1.5 text-left transition-colors ${
                        isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-slate-50'
                      }`}
                    >
                      <p className="truncate text-[10px] font-mono text-slate-400">
                        {row.campaign_id}
                      </p>
                      <p className="truncate text-[11.5px] font-medium text-slate-700">
                        {row.nameplate}
                      </p>
                      <p className="truncate text-[10px] text-slate-400">
                        {row.quarter} {row.platform} · {row.format} · {row.size}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BriefingRowsSkeleton() {
  return (
    <div className="flex flex-col gap-2 p-1">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex flex-col gap-1 rounded px-2 py-1.5">
          <div className="h-3 w-3/4 animate-pulse rounded bg-slate-200" />
          <div className="h-2.5 w-1/2 animate-pulse rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}
